import { useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { QA_NAME_ATTACH_BTN, QA_NAME_CANCEL_BTN } from '../../../constants/qa'
import { AIGretaAskButton, AIGretaLoader } from '../../AIGreta'
import { TableRowActions } from '../../Table'
import { TableModal } from '../../TableModal'
import { useAttachServiceRequestModal } from '../Request.hooks'

import {
  ATTACH_SERVICE_REQUESTS_SEARCH_MAX_LENGTH,
  QA_NAME_ATTACH_SERVICE_REQUESTS_AI_BUTTON,
  QA_NAME_ATTACH_SERVICE_REQUESTS_MODAL,
} from './AttachServiceRequestsModal.constants'
import { useAttachServiceRequestsAISuggest } from './AttachServiceRequestsModal.hooks'
import { AttachServiceRequestsModalAISuggestionsPanel } from './AttachServiceRequestsModalAISuggestionsPanel'
import { AttachServiceRequestsModalTableRow } from './AttachServiceRequestsModalTableRow'

import * as styles from './AttachServiceRequestsModal.module.css'

import ServiceRequestIcon from '../../../icons/service-request.svg'

function AttachServiceRequestsModal({
  Actions,
  actionsProps,
  aiSuggestEmailUuid,
  allowEmptySelection,
  counterProps,
  excludeIds,
  headerProps,
  multipleSelection,
  readonly,
  ref,
  onClose,
  ...restProps
}) {
  const {
    extra,
    handleApplyConfirmModal,
    handleAttach,
    handleCloseTableModal,
    isCloseConfirm,
    loadData,
    modal,
    setCloseConfirm,
    setNotificationListRef,
    tableColumns,
  } = useAttachServiceRequestModal({
    ...restProps,
    allowEmptySelection,
    excludeIds,
    multipleSelection,
    readonly,
    onClose,
  })

  const { t } = useTranslation()

  const {
    canShowSearchInput,
    checkIsItemSelected,
    exclude,
    handleChangePagination,
    handleChangeSearchValue,
    handleClearSearch,
    handleExclude,
    handleLoadItems,
    handlePrependNotification,
    handleRemoveNotification,
    handleSelect,
    handleSelectAll,
    handleSetOrdering,
    handleUnselectAllPages,
    isAllSelected,
    isInitiatedError,
    isInitLoading,
    isRemoveSelection,
    loading,
    notifications,
    ordering,
    pagination,
    search,
    selectAllPages,
    selected,
    setLoading,
    tableItems,
  } = loadData

  useImperativeHandle(
    ref,
    () => ({
      refresh: handleLoadItems,
      selected,
      setLoading,
      unselectAll: handleUnselectAllPages,
    }),
    [handleLoadItems, selected, setLoading]
  )

  function handleReplaceSelection(item) {
    handleUnselectAllPages()
    handleSelect(item, true)
  }

  const {
    handleClearAISelection,
    handleCollapseToggle,
    handleDropAISelectionMark,
    handleRequestSuggestions,
    handleSelectSuggestion,
    isAIAvailable,
    isCollapsed,
    isSuggestLoading,
    suggestions,
  } = useAttachServiceRequestsAISuggest({
    emailMessageUuid: aiSuggestEmailUuid,
    excludeIds,
    selected,
    tableItems,
    onPrependNotification: handlePrependNotification,
    onRemoveNotification: handleRemoveNotification,
    onReplaceSelection: handleReplaceSelection,
    onUnselectAll: handleUnselectAllPages,
  })

  function handleChangeSearch(event) {
    handleClearAISelection()
    handleChangeSearchValue(event)
  }

  function handleClearSearchValue() {
    handleClearAISelection()
    handleClearSearch()
  }

  function handleSelectRow(item, isChecked) {
    handleDropAISelectionMark()
    handleSelect(item, isChecked)
  }

  const aiSuggestOverlay =
    isAIAvailable && isSuggestLoading ? (
      <AIGretaLoader className={styles.aiSuggestLoaderOverlay} />
    ) : null

  const isAttachAction = !readonly && tableItems?.items?.length !== 0

  const hasSelection = selected.length > 0 || selectAllPages

  const aiSuggestContent =
    isAIAvailable && suggestions ? (
      <AttachServiceRequestsModalAISuggestionsPanel
        isCollapsed={isCollapsed}
        selectedId={selected[0]?.id}
        suggestions={suggestions}
        onCollapseToggle={handleCollapseToggle}
        onRefresh={handleRequestSuggestions}
        onSelect={handleSelectSuggestion}
      />
    ) : null

  return (
    <>
      {modal}
      <TableModal
        {...(readonly ? { SelectionToolbar: null } : null)}
        {...(canShowSearchInput ? null : { Search: null })}
        {...(counterProps
          ? { counterProps: { ...counterProps, count: pagination?.count } }
          : null)}
        initialFullScreen
        Actions={Actions}
        actionsProps={{
          ...actionsProps,
          ButtonClear: false,
          buttonAttachProps: {
            ...actionsProps?.buttonAttachProps,
            disabled:
              isSuggestLoading ||
              (isAttachAction && !hasSelection && !allowEmptySelection),
            isLoading: loading,
            qaName: QA_NAME_ATTACH_BTN,
            text:
              actionsProps?.buttonAttachProps?.text ||
              'Modal.ServiceRequestsAttachBtnKey',
            onClick: isAttachAction ? handleAttach : onClose,
          },
          buttonCancelProps: {
            qaName: QA_NAME_CANCEL_BTN,
            onClick: handleCloseTableModal,
          },
          componentProps: {
            ...actionsProps?.componentProps,
            className: classNames(
              styles.footer,
              actionsProps?.componentProps?.className
            ),
          },
        }}
        beforeTableContent={aiSuggestContent}
        contentProps={{ loading }}
        emptyStateProps={{
          EntityIcon: ServiceRequestIcon,
          className: styles.emptyState,
          text: t('Modal.AttachServiceRequestsModalNoDataKey'),
        }}
        headerProps={{
          ...headerProps,
          ...(isAIAvailable && !suggestions
            ? {
                actions: (
                  <AIGretaAskButton
                    disabled={
                      isInitLoading || isSuggestLoading || !canShowSearchInput
                    }
                    isLoading={isSuggestLoading}
                    qaName={QA_NAME_ATTACH_SERVICE_REQUESTS_AI_BUTTON}
                    onClick={handleRequestSuggestions}
                  />
                ),
              }
            : null),
          text: headerProps?.text || 'Modal.AttachServiceRequestsModalTitleKey',
          onClose: handleCloseTableModal,
        }}
        isCloseConfirm={isCloseConfirm}
        isInitiatedError={isInitiatedError}
        isInitLoading={isInitLoading}
        loading={loading}
        notificationListProps={{
          ref: setNotificationListRef,
        }}
        notifications={notifications}
        overlayContent={aiSuggestOverlay}
        paginationProps={{ pagination, onChange: handleChangePagination }}
        qaName={QA_NAME_ATTACH_SERVICE_REQUESTS_MODAL}
        searchProps={{
          maxLength: ATTACH_SERVICE_REQUESTS_SEARCH_MAX_LENGTH,
          value: search,
          onChange: handleChangeSearch,
          onClear: handleClearSearchValue,
        }}
        selectAllPages={selectAllPages}
        selected={selected}
        selectionToolbarProps={{
          exclude,
          pagination,
          selectAllPages,
          selected,
          onClose: handleUnselectAllPages,
        }}
        setCloseConfirm={setCloseConfirm}
        tableItems={tableItems?.items}
        tableItemsCount={pagination.page}
        tableProps={{
          ...(readonly ? { actionsColumnWidth: 0 } : null),
          TableRow: AttachServiceRequestsModalTableRow,
          checkIsItemSelected,
          className: styles.table,
          columns: tableColumns,
          headerRowProps: {
            ...(!readonly && multipleSelection
              ? { Actions: TableRowActions }
              : null),
            actionsProps: {
              checked: isAllSelected,
              selected: isRemoveSelection,
              onChange: handleSelectAll,
            },
            ordering,
            resizable: false,
            withCellBorder: false,
            onSetOrdering: handleSetOrdering,
          },
          items: tableItems?.items,
          rowProps: {
            ...(readonly ? { Actions: null } : null),
            disabled: multipleSelection ? false : selected.length > 0,
            extra,
            readonly,
            onSelect: selectAllPages ? handleExclude : handleSelectRow,
          },
        }}
        onApplyConfirmModal={handleApplyConfirmModal}
        onRefresh={handleLoadItems}
        onTableModalClose={handleCloseTableModal}
      />
    </>
  )
}

export default AttachServiceRequestsModal
